import { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  FileText,
  Clock,
  CheckCircle2,
  AlertCircle,
  Mail,
  TrendingDown,
  CreditCard,
  Search,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { InvoiceDetailModal } from "@/components/modals/InvoiceDetailModal";
import { exportInvoicesCsv } from "@/lib/csvExport";

const PAGE_SIZE = 25;

interface Invoice {
  id: string;
  payment_amount: number;
  payment_due_date: string;
  supplier_name: string;
  supplier_email: string;
  status: string;
  apr_rate: number;
  discount_amount: number | null;
  discounted_payment_amount: number | null;
  email_sent_at: string | null;
  email_error: string | null;
  created_at: string;
}

interface InvoiceListProps {
  invoices: Invoice[];
  isLoading: boolean;
  availableBudget?: number;
  /** IDs of invoices to briefly highlight (e.g. just uploaded) */
  highlightedInvoiceIds?: string[];
}

type StatusFilter =
  | "active"
  | "all"
  | "pending"
  | "early_payment_scheduled"
  | "early_payment_completed"
  | "standard_payment";

const statusFilters: { value: StatusFilter; label: string }[] = [
  { value: "active", label: "Active" },
  { value: "all", label: "All" },
  { value: "pending", label: "Pending" },
  { value: "early_payment_scheduled", label: "Early Payment" },
  { value: "early_payment_completed", label: "Completed - Accelerated" },
  { value: "standard_payment", label: "Completed - Standard" },
];

export function InvoiceList({ invoices, isLoading, highlightedInvoiceIds = [] }: InvoiceListProps) {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [page, setPage] = useState(0);

  const filteredInvoices = useMemo(() => {
    let result = invoices;

    if (statusFilter === "active") {
      result = result.filter(
        (inv) =>
          inv.status === "pending" ||
          inv.status === "early_payment_scheduled" ||
          inv.status === "early_payment"
      );
    } else if (statusFilter === "early_payment_scheduled") {
      result = result.filter(
        (inv) => inv.status === "early_payment_scheduled" || inv.status === "early_payment"
      );
    } else if (statusFilter === "early_payment_completed") {
      result = result.filter(
        (inv) => inv.status === "early_payment_completed" || inv.status === "paid"
      );
    } else if (statusFilter !== "all") {
      result = result.filter((inv) => inv.status === statusFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (inv) =>
          inv.supplier_name.toLowerCase().includes(q) ||
          inv.supplier_email.toLowerCase().includes(q) ||
          inv.payment_amount.toString().includes(q)
      );
    }

    return result;
  }, [invoices, statusFilter, searchQuery]);

  const totalPages = Math.ceil(filteredInvoices.length / PAGE_SIZE);
  const pagedInvoices = filteredInvoices.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  const handleFilterChange = (f: StatusFilter) => {
    setStatusFilter(f);
    setPage(0);
  };
  const handleSearchChange = (q: string) => {
    setSearchQuery(q);
    setPage(0);
  };

  const activeCount = invoices.filter(
    (inv) =>
      inv.status === "pending" ||
      inv.status === "early_payment_scheduled" ||
      inv.status === "early_payment"
  ).length;

  if (isLoading) {
    return (
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: "hsl(224 40% 7%)",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.4)",
        }}
      >
        <div className="flex items-center justify-center py-12">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-12 h-12 bg-white/[0.06] rounded-full" />
            <div className="h-4 w-32 bg-white/[0.06] rounded" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-xl overflow-hidden h-fit"
      style={{
        background: "hsl(224 40% 7%)",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 0 0 1px rgba(255,255,255,0.05), 0 4px 24px rgba(0,0,0,0.4)",
      }}
    >
      <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
        <div>
          <h2 className="text-base font-display font-semibold text-white/90 tracking-tight">
            Invoices
          </h2>
          <p className="text-[11px] text-white/40 mt-0.5">
            {statusFilter === "active"
              ? `${activeCount} active`
              : `${filteredInvoices.length.toLocaleString()} of ${invoices.length.toLocaleString()}`}{" "}
            invoices
          </p>
        </div>
        {filteredInvoices.length > 0 && (
          <Button
            variant="ghost"
            size="sm"
            className="h-8 text-[11px] text-white/50 hover:text-white/80 gap-1.5"
            onClick={() => exportInvoicesCsv(filteredInvoices)}
          >
            <Download className="w-3.5 h-3.5" />
            Export CSV
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="px-4 pt-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-white/30" />
          <Input
            placeholder="Search supplier, email, amount..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="h-8 text-xs pl-8 pr-8 bg-white/[0.04] border-white/[0.08] text-white/80 placeholder:text-white/25 focus-visible:ring-indigo-500/30"
          />
          {searchQuery && (
            <button
              onClick={() => handleSearchChange("")}
              className="absolute right-2.5 top-1/2 -translate-y-1/2"
            >
              <X className="w-3.5 h-3.5 text-white/30 hover:text-white/60" />
            </button>
          )}
        </div>
      </div>

      {/* Filter chips */}
      <div className="px-4 pt-2 pb-1 flex flex-wrap gap-1.5">
        {statusFilters.map((opt) => (
          <button
            key={opt.value}
            onClick={() => handleFilterChange(opt.value)}
            className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
              statusFilter === opt.value
                ? "bg-indigo-500/20 text-indigo-300 border-indigo-500/30"
                : "bg-white/[0.04] text-white/40 border-white/[0.08] hover:bg-white/[0.07] hover:text-white/60"
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>

      {filteredInvoices.length === 0 ? (
        <div className="text-center py-12 px-4">
          <div className="w-14 h-14 mx-auto mb-3 rounded-full bg-white/[0.05] flex items-center justify-center">
            <FileText className="w-7 h-7 text-white/30" />
          </div>
          <h3 className="text-sm font-display font-semibold text-white/70 mb-1">
            {searchQuery ? "No matches" : "No invoices"}
          </h3>
          <p className="text-xs text-white/40">
            {searchQuery ? "Try a different search" : "Upload an invoice to get started"}
          </p>
        </div>
      ) : (
        <>
          <ActiveInvoiceList
            invoices={pagedInvoices}
            onInvoiceClick={(inv) => setSelectedInvoice(inv)}
            highlightedInvoiceIds={highlightedInvoiceIds}
          />
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-2.5 border-t border-white/[0.06]">
              <p className="text-[11px] text-white/40">
                {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredInvoices.length)}{" "}
                of {filteredInvoices.length.toLocaleString()} invoices
              </p>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-white/40 hover:text-white/70 hover:bg-white/[0.06]"
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </Button>
                <span className="text-[11px] text-white/40 px-1">
                  {page + 1} / {totalPages}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-white/40 hover:text-white/70 hover:bg-white/[0.06]"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage((p) => p + 1)}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <InvoiceDetailModal
        open={!!selectedInvoice}
        onOpenChange={(open) => !open && setSelectedInvoice(null)}
        invoice={selectedInvoice}
      />
    </div>
  );
}

/** Detailed active-invoice list with status badges, error states, etc. */
function ActiveInvoiceList({
  invoices,
  onInvoiceClick,
  highlightedInvoiceIds = [],
}: {
  invoices: Invoice[];
  onInvoiceClick: (inv: Invoice) => void;
  highlightedInvoiceIds?: string[];
}) {
  const getStatusConfig = (status: string, emailError: string | null) => {
    if (emailError)
      return {
        label: "Email Failed",
        icon: AlertCircle,
        pillClass: "bg-red-500/15 text-red-300 border border-red-500/20",
      };
    switch (status) {
      case "pending":
        return {
          label: "Pending",
          icon: Clock,
          pillClass: "bg-amber-500/15 text-amber-300 border border-amber-500/20",
        };
      case "early_payment_scheduled":
      case "early_payment":
        return {
          label: "Early Payment",
          icon: CheckCircle2,
          pillClass: "bg-indigo-500/15 text-indigo-300 border border-indigo-500/20",
        };
      case "early_payment_completed":
      case "paid":
        return {
          label: "Completed - Accelerated",
          icon: CreditCard,
          pillClass: "bg-emerald-500/15 text-emerald-300 border border-emerald-500/20",
        };
      case "standard_payment":
        return {
          label: "Completed - Standard",
          icon: FileText,
          pillClass: "bg-white/[0.05] text-white/40 border border-white/[0.08]",
        };
      default:
        return {
          label: status,
          icon: FileText,
          pillClass: "bg-white/[0.05] text-white/40 border border-white/[0.08]",
        };
    }
  };

  const formatCurrency = (amount: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(amount);

  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), "MMM d, yyyy");
    } catch {
      return dateString;
    }
  };

  const getAcceleratedInfo = (invoice: Invoice) => {
    if (invoice.status === "early_payment_scheduled" || invoice.status === "early_payment")
      return { isAccelerated: true, reason: "Accepted by supplier" };
    if (invoice.status === "early_payment_completed" || invoice.status === "paid")
      return { isAccelerated: true, reason: "Paid via ACH" };
    if (invoice.status === "pending" && invoice.email_error?.includes("Insufficient budget"))
      return { isAccelerated: false, reason: "Insufficient budget" };
    if (invoice.status === "pending")
      return { isAccelerated: true, reason: "Offer sent to supplier" };
    return { isAccelerated: false, reason: "" };
  };

  return (
    <div className="max-h-[600px] overflow-y-auto">
      {/* Table header */}
      <div
        className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-4 py-2.5 border-b border-white/[0.06]"
        style={{ background: "rgba(255,255,255,0.03)" }}
      >
        <span className="text-[11px] uppercase tracking-widest text-white/40 font-medium">
          Supplier
        </span>
        <span className="text-[11px] uppercase tracking-widest text-white/40 font-medium text-right">
          Amount
        </span>
        <span className="text-[11px] uppercase tracking-widest text-white/40 font-medium text-right">
          Status
        </span>
      </div>

      {invoices.map((invoice) => {
        const statusConfig = getStatusConfig(invoice.status, invoice.email_error);
        const StatusIcon = statusConfig.icon;
        const accInfo = getAcceleratedInfo(invoice);
        const savingsPercent =
          invoice.payment_amount > 0 && invoice.discount_amount
            ? ((invoice.discount_amount / invoice.payment_amount) * 100).toFixed(2)
            : null;

        const isHighlighted = highlightedInvoiceIds.includes(invoice.id);
        return (
          <div
            key={invoice.id}
            className={`border-b border-white/[0.04] hover:bg-white/[0.03] transition-colors duration-100 cursor-pointer ${isHighlighted ? "bg-emerald-500/10" : ""}`}
            onClick={() => onInvoiceClick(invoice)}
          >
            <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 px-4 py-3 items-start">
              {/* Supplier info */}
              <div className="min-w-0">
                <div className="flex items-center gap-2 min-w-0">
                  <div className="w-7 h-7 rounded-md bg-white/[0.06] flex items-center justify-center flex-shrink-0">
                    <FileText className="w-3.5 h-3.5 text-white/40" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-white/90 truncate">
                      {invoice.supplier_name}
                    </p>
                    <p className="text-[11px] text-white/40 truncate">{invoice.supplier_email}</p>
                  </div>
                </div>

                <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1.5 ml-9 text-[11px]">
                  <span className="text-white/40">
                    Due:{" "}
                    <span className="text-white/60 font-mono tabular-nums">
                      {formatDate(invoice.payment_due_date)}
                    </span>
                  </span>
                  {savingsPercent && (
                    <>
                      <span className="text-white/20">•</span>
                      <span className="text-emerald-400 font-medium flex items-center gap-0.5">
                        <TrendingDown className="w-2.5 h-2.5" />
                        {savingsPercent}% savings
                      </span>
                    </>
                  )}
                </div>

                {invoice.discounted_payment_amount &&
                  (invoice.status === "early_payment_scheduled" ||
                    invoice.status === "early_payment") && (
                    <p className="text-[11px] text-emerald-400 mt-1 ml-9 font-mono tabular-nums">
                      Pay {formatCurrency(invoice.discounted_payment_amount)}
                      <span className="text-white/30 ml-1">
                        (save {formatCurrency(invoice.discount_amount || 0)})
                      </span>
                    </p>
                  )}
                {invoice.discounted_payment_amount &&
                  invoice.status === "pending" &&
                  !invoice.email_error && (
                    <p className="text-[11px] text-white/40 mt-1 ml-9 font-mono tabular-nums">
                      Offered: {formatCurrency(invoice.discounted_payment_amount)}
                    </p>
                  )}

                {!accInfo.isAccelerated && accInfo.reason && (
                  <div className="mt-1.5 ml-9 p-1.5 bg-amber-500/10 rounded text-[11px] text-amber-300 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    {accInfo.reason} — offer not sent
                  </div>
                )}

                {invoice.email_error && (
                  <div className="mt-1.5 ml-9 p-1.5 bg-red-500/10 rounded text-[11px] text-red-300 flex items-center gap-1">
                    <AlertCircle className="w-3 h-3 flex-shrink-0" />
                    {invoice.email_error}
                  </div>
                )}

                {invoice.email_sent_at && !invoice.email_error && (
                  <div className="mt-1.5 ml-9 flex items-center gap-1 text-[11px] text-white/30">
                    <Mail className="w-3 h-3" />
                    Sent {formatDate(invoice.email_sent_at)}
                  </div>
                )}
              </div>

              {/* Amount */}
              <div className="text-right pt-0.5">
                <span className="text-sm font-mono tabular-nums font-semibold text-white/80">
                  {formatCurrency(invoice.payment_amount)}
                </span>
              </div>

              {/* Status badge */}
              <div className="flex items-start justify-end pt-0.5">
                <span
                  className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-medium ${statusConfig.pillClass}`}
                >
                  <StatusIcon className="w-2.5 h-2.5" />
                  {statusConfig.label}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
